/**
 * playerAbilityCastController — Wire QWER to liveAbilityCast (kit by championKey).
 * Single path for all champs (AB-09) — no Ashe legacy dual-path.
 * Projectile meshes resolve via abilityVfx registry (AB-03).
 */

import { Projectile } from '../../domain/projectile.js';
import { createAbilityVfxMesh } from '../vfx/abilityVfx.js';
import { getAbilityVfxSpec } from '../vfx/abilityVfxSpecs.js';
import '../vfx/abilityVfxPaintersBoot.js';
import {
  castLiveAbility,
  resolveChampionKey
} from '../../application/liveAbilityCast.js';
import { LIVE_WORLD_SCALE } from '../../application/liveAbilityPlane.js';
import { getAbilityByChampSlot } from '../../domain/abilities/abilityCatalog.js';
import { targetingFamily } from '../../domain/abilities/abilitySpec.js';
import { clampGroundCast } from '../../domain/combat/abilityArea.js';

function clampGroundCursor(champion, abilityKey, cursor) {
  if (!champion || !cursor) return cursor;
  const spec = getAbilityByChampSlot(resolveChampionKey(champion), abilityKey);
  if (!spec || targetingFamily(spec.targeting) !== 'ground') return cursor;
  const oy = Number.isFinite(champion.z) ? champion.z : (champion.y ?? 0);
  const ty = cursor.z ?? cursor.y;
  const proxy = { x: champion.x, y: oy, liveWorldScale: LIVE_WORLD_SCALE };
  const aim = clampGroundCast(proxy, cursor.x, ty, spec.range);
  return { x: aim.x, z: aim.y, y: aim.y };
}

function resolveProjectileThree(deps) {
  return deps.THREE || globalThis.THREE || null;
}

function spawnLiveSkillshot(scene, activeProjectiles, sourceEntity, spec, THREE, onHit) {
  const type = spec.type || 'skillshot_w';
  const pDomain = new Projectile({
    id: spec.id,
    sourceEntity,
    type,
    angle: spec.angle,
    speed: spec.speed,
    maxDistance: spec.maxDistance,
    damage: spec.damage,
    targetX: spec.targetX,
    targetZ: spec.targetZ,
    hitRadius: spec.hitRadius
  });
  const pMesh = createAbilityVfxMesh(spec.vfxId, THREE);
  if (!pMesh) return;
  scene.add(pMesh);
  const entry = { domain: pDomain, mesh: pMesh };
  if (typeof onHit === 'function') entry.onHit = onHit;
  activeProjectiles.push(entry);
}

function playResultCasterVfx(deps, result, championEntity, cursor) {
  if (!result?.vfxId || typeof deps.playCasterVfx !== 'function') return;
  const spec = getAbilityVfxSpec(result.vfxId);
  if (!spec || !['aura', 'burst', 'ground'].includes(spec.kind)) return;
  deps.playCasterVfx(result.vfxId, championEntity, {
    target: championEntity.attackTarget || null,
    ground: cursor || null
  });
}

function spawnResultProjectiles(deps, result, championEntity, THREE) {
  for (const spec of result.liveProjectiles) {
    const vfxId = spec.vfxId;
    spawnLiveSkillshot(
      deps.scene,
      deps.activeProjectiles,
      championEntity,
      spec,
      THREE,
      typeof deps.playAbilityHitSfx === 'function' && vfxId
        ? () => deps.playAbilityHitSfx(vfxId)
        : null
    );
  }
}

function playResultAbilitySfx(deps, result) {
  if (!result?.vfxId || typeof deps.playAbilitySfx !== 'function') return;
  const spec = getAbilityVfxSpec(result.vfxId);
  deps.playAbilitySfx(result.vfxId, spec);
}

/**
 * @param {{
 *   getChampion: () => object,
 *   getMouse: () => { x: number, z: number },
 *   getEntities: () => object[],
 *   getGameTime?: () => number,
 *   scene: object,
 *   activeProjectiles: object[],
 *   THREE?: object,
 *   playCasterVfx?: Function,
 *   playAbilitySfx?: Function,
 *   playAbilityHitSfx?: Function
 * }} deps
 */
export function createPlayerAbilityCastController(deps) {
  function cast(abilityKey) {
    const championEntity = deps.getChampion();
    if (!championEntity || championEntity.isDead) return;
    const mouse = deps.getMouse() || { x: championEntity.x, z: championEntity.z };
    const cursor = clampGroundCursor(championEntity, abilityKey, mouse);
    const entities = deps.getEntities ? deps.getEntities() : [];
    const gameTime = deps.getGameTime ? deps.getGameTime() : 0;
    const THREE = resolveProjectileThree(deps);
    const key = resolveChampionKey(championEntity);
    const result = castLiveAbility({
      player: championEntity,
      champKey: key,
      abilityKey,
      cursor,
      gameTime,
      entities,
      selectedTarget: championEntity.attackTarget || null
    });
    if (!result) return;
    playResultCasterVfx(deps, result, championEntity, cursor);
    playResultAbilitySfx(deps, result);
    spawnResultProjectiles(deps, result, championEntity, THREE);
  }

  return {
    cast,
    onActivateQ: () => cast('Q'),
    onCastW: () => cast('W'),
    onCastE: () => cast('E'),
    onCastR: () => cast('R')
  };
}
